import { z } from 'zod';

import { BusinessType, LegalForm } from '@/constants/institution';
import {
  institutionAddressSchema,
  institutionAddressToInstitutionDetailsAddressApi,
  institutionDetailsAddressApiToInstitutionAddress,
} from '@/features/institution/schemas/address';
import i18n from '@/lib/i18n';
import {
  type InstitutionDetailsCompany,
  type InstitutionDetailsV2Company,
  institutionDetailsV2CompanySchema,
  withInstitutionDetailsV2LegalFormRefinements,
  withInstitutionDetailsV2TypeOfBusinessRefinements,
} from '@/services/api/profile';
import { isDatePartsValid, toDatePartsFromString, toDateStringFromParts } from '@/utils/date';
import { addFieldIssue } from '@/utils/field-issues';

// Each cross-field check parses only the answers it reads, so it still runs while another answer is invalid
const institutionCompanyLegalFormSchema = withInstitutionDetailsV2LegalFormRefinements(
  z.object({
    legalForm: institutionDetailsV2CompanySchema.shape.legalForm,
    legalFormOther: z.string(),
  }),
).superRefine(({ legalForm, legalFormOther }, context) => {
  if (legalForm === LegalForm.OTHER && legalFormOther) {
    addFieldIssue(context, ['legalFormOther'], institutionDetailsV2CompanySchema.shape.legalFormOther.unwrap(), legalFormOther);
  }
});

const institutionCompanyTypeOfBusinessSchema = withInstitutionDetailsV2TypeOfBusinessRefinements(
  z.object({
    typeOfBusiness: institutionDetailsV2CompanySchema.shape.typeOfBusiness,
    subTypeOfBusiness: institutionDetailsV2CompanySchema.shape.subTypeOfBusiness,
    typeOfBusinessOther: z.string(),
  }),
).superRefine(({ typeOfBusiness, typeOfBusinessOther }, context) => {
  if (typeOfBusiness === BusinessType.OTHER && typeOfBusinessOther) {
    addFieldIssue(context, ['typeOfBusinessOther'], institutionDetailsV2CompanySchema.shape.typeOfBusinessOther.unwrap(), typeOfBusinessOther);
  }
});

export const institutionCompanySchema = z
  .object({
    companyName: institutionDetailsV2CompanySchema.shape.companyName,
    registeredAddress: institutionAddressSchema,
    registrationNumber: institutionDetailsV2CompanySchema.shape.registrationNumber,
    dateOfIncorporation: z
      .object({
        day: z.string().regex(/^\d{1,2}$/, i18n.t('institutionDetails.company.errors.dateOfIncorporation')),
        month: z.string().regex(/^\d{1,2}$/, i18n.t('institutionDetails.company.errors.dateOfIncorporation')),
        year: z.string().regex(/^\d{4}$/, i18n.t('institutionDetails.company.errors.dateOfIncorporation')),
      })
      .refine(isDatePartsValid, { error: i18n.t('institutionDetails.company.errors.dateOfIncorporation'), when: ({ issues }) => issues.length === 0 }),
    taxId: institutionDetailsV2CompanySchema.shape.taxId,
    leiNumber: z
      .union([institutionDetailsV2CompanySchema.shape.leiNumber, z.literal('')], {
        error: i18n.t('institutionDetails.company.errors.leiNumberInvalid'),
      })
      .transform((value) => (value === '' ? undefined : value)),
    website: z
      .union([institutionDetailsV2CompanySchema.shape.website, z.literal('')], {
        error: i18n.t('institutionDetails.company.errors.website'),
      })
      .transform((value) => (value === '' ? undefined : value)),
  })
  .and(institutionCompanyLegalFormSchema)
  .and(institutionCompanyTypeOfBusinessSchema);

export type InstitutionCompany = z.input<typeof institutionCompanySchema>;

export type InstitutionCompanyOutput = z.output<typeof institutionCompanySchema>;

export const institutionCompanyToInstitutionDetailsCompanyApi = ({
  registeredAddress,
  dateOfIncorporation,
  legalFormOther,
  typeOfBusinessOther,
  ...company
}: InstitutionCompanyOutput): InstitutionDetailsV2Company => ({
  ...company,
  ...(company.legalForm === LegalForm.OTHER && { legalFormOther }),
  ...(company.typeOfBusiness === BusinessType.OTHER && { typeOfBusinessOther }),
  registeredAddress: institutionAddressToInstitutionDetailsAddressApi(registeredAddress),
  dateOfIncorporation: toDateStringFromParts(dateOfIncorporation),
});

export const institutionDetailsLockedCompanyToInstitutionCompany = ({ registeredAddress, ...company }: Partial<InstitutionDetailsCompany>): Partial<InstitutionCompany> => ({
  ...company,
  ...(registeredAddress && typeof registeredAddress !== 'string' && { registeredAddress: institutionDetailsAddressApiToInstitutionAddress(registeredAddress) }),
});

export const institutionDetailsCompanyApiToInstitutionCompany = ({ registeredAddress, dateOfIncorporation, ...company }: InstitutionDetailsV2Company): InstitutionCompany => ({
  ...company,
  legalFormOther: company.legalFormOther ?? '',
  registeredAddress: institutionDetailsAddressApiToInstitutionAddress(registeredAddress),
  dateOfIncorporation: toDatePartsFromString(dateOfIncorporation),
  typeOfBusinessOther: company.typeOfBusinessOther ?? '',
  leiNumber: company.leiNumber ?? '',
  website: company.website ?? '',
});
